import { toPng } from 'html-to-image';

/** Aspect ratio for the ID portrait window — matches the lockup's 266×322
 * photo slot (19:23 in lowest terms) so the captured framing maps 1:1 to
 * the slot with no center-crop. */
export const PHOTO_ASPECT_W = 19;
export const PHOTO_ASPECT_H = 23;
export const PHOTO_ASPECT = PHOTO_ASPECT_W / PHOTO_ASPECT_H;

/** Pixel resolution we render the cropped portrait at (retina-safe for ID + share use). */
export const PHOTO_OUTPUT_WIDTH = 800;
export const PHOTO_OUTPUT_HEIGHT = Math.round(PHOTO_OUTPUT_WIDTH / PHOTO_ASPECT);

/** Load an image element from a data URL or object URL. */
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/**
 * Center-crops an image to PHOTO_ASPECT and returns a JPEG data URL at PHOTO_OUTPUT_WIDTH×HEIGHT.
 * Used for camera-captured frames where we already framed against the guide overlay.
 */
export function centerCropToOutput(source: HTMLCanvasElement | HTMLImageElement): string {
  const sw = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const sh = 'naturalHeight' in source ? source.naturalHeight : source.height;

  const sourceAspect = sw / sh;
  let cropW = sw;
  let cropH = sh;
  if (sourceAspect > PHOTO_ASPECT) {
    cropW = sh * PHOTO_ASPECT;
  } else {
    cropH = sw / PHOTO_ASPECT;
  }
  const cropX = (sw - cropW) / 2;
  const cropY = (sh - cropH) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = PHOTO_OUTPUT_WIDTH;
  canvas.height = PHOTO_OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');
  ctx.drawImage(source, cropX, cropY, cropW, cropH, 0, 0, PHOTO_OUTPUT_WIDTH, PHOTO_OUTPUT_HEIGHT);
  return canvas.toDataURL('image/jpeg', 0.92);
}

export interface CropTransform {
  /** Image natural width (px). */
  imgW: number;
  /** Image natural height (px). */
  imgH: number;
  /** User-applied scale multiplier (>= "fit" scale). */
  scale: number;
  /** Translation in display px from center. */
  offsetX: number;
  /** Translation in display px from center. */
  offsetY: number;
  /** Crop frame display width (px). */
  frameW: number;
  /** Crop frame display height (px). */
  frameH: number;
  /** Min scale: image fully covers the frame. */
  minScale: number;
}

/**
 * Renders the cropped image based on the cropper's display transform.
 * The output is PHOTO_OUTPUT_WIDTH×HEIGHT.
 */
export function renderCrop(image: HTMLImageElement, transform: CropTransform): string {
  const { imgW, imgH, scale, offsetX, offsetY, frameW, frameH } = transform;

  // Image displayed size at the user's chosen scale.
  const displayW = imgW * scale;
  const displayH = imgH * scale;

  // Display-space coordinates of the image's top-left relative to the frame center.
  const imgLeft = -displayW / 2 + offsetX;
  const imgTop = -displayH / 2 + offsetY;

  // Frame's top-left relative to its own center (always negative half).
  const frameLeft = -frameW / 2;
  const frameTop = -frameH / 2;

  // Convert frame's coordinates back to source-image coordinates.
  const srcX = ((frameLeft - imgLeft) / displayW) * imgW;
  const srcY = ((frameTop - imgTop) / displayH) * imgH;
  const srcW = (frameW / displayW) * imgW;
  const srcH = (frameH / displayH) * imgH;

  const canvas = document.createElement('canvas');
  canvas.width = PHOTO_OUTPUT_WIDTH;
  canvas.height = PHOTO_OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context');
  ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, PHOTO_OUTPUT_WIDTH, PHOTO_OUTPUT_HEIGHT);
  return canvas.toDataURL('image/jpeg', 0.92);
}

/** Returns true if the device most likely has a usable getUserMedia camera. */
export function isCameraSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/** Rasterize the offscreen IDCard lockup to a PNG data URL at its natural
 * 695×1037 size. Waits for embedded `<img>`s to decode first so Safari
 * doesn't capture before the photo paints. */
export async function rasterizeLockup(el: HTMLElement): Promise<string> {
  await waitForImageDecodes(el);
  return toPng(el, { cacheBust: true, pixelRatio: 1, skipFonts: true });
}

/**
 * Wait for every `<img>` inside `root` to be decoded and renderable. Safari
 * frequently runs html-to-image's capture before a freshly-set data-URL
 * `<img>` has finished decoding, which causes the lockup PNG to come out
 * without the photo. Awaiting `decode()` blocks until the image is ready.
 */
export async function waitForImageDecodes(root: Element | null | undefined): Promise<void> {
  if (!root) return;
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map((img) => {
      if (typeof img.decode === 'function') {
        return img.decode().catch(() => undefined);
      }
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      });
    }),
  );
}
